import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { X } from "lucide-react";
import DocxViewer from "./DocxViewer";

interface ViewDocumentModalProps {
  open: boolean;
  onClose: () => void;
  documentUrl?: string | { title: string; url: string }[];
  title?: string;
  loading?: boolean;
  mimeType?: string;
}

const ViewDocumentModal: React.FC<ViewDocumentModalProps> = ({
  open,
  onClose,
  documentUrl,
  title,
  loading = false,
  mimeType,
}) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [objectMime, setObjectMime] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  const normalizeToData = useMemo(() => {
    // Normalize documentUrl into an object with {fileName?, mime?, dataUrl}
    if (!documentUrl)
      return null as null | {
        fileName?: string;
        mime?: string;
        dataUrl: string;
      };

    if (typeof documentUrl === "string") {
      const s = documentUrl;
      if (s.startsWith("data:")) {
        // data:<mime>;base64,...
        const mime = s.substring(5, s.indexOf(";")) || undefined;
        return { dataUrl: s, mime };
      }
      // Could be bare base64 (no prefix) or a remote URL.
      // If it looks like base64 (long string with no spaces), try to guess PDF first.
      const isProbablyBase64 = /^[A-Za-z0-9+/=\r\n]+$/.test(
        s.replace(/^\s+|\s+$/g, ""),
      );
      if (isProbablyBase64) {
        // assume PDF if unknown — fallback will show download
        return {
          dataUrl: `data:application/pdf;base64,${s}`,
          mime: "application/pdf",
        };
      }
      // treat as remote URL
      return { dataUrl: s, mime: undefined };
    }

    // array handled elsewhere in render path (each entry has title/url)
    return null as null | { fileName?: string; mime?: string; dataUrl: string };
  }, [documentUrl]);

  // When we have a data: URL for a non-previewable mime (eg word docs), convert to blob once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!normalizeToData) return;
        const { dataUrl, mime } = normalizeToData;
        if (!dataUrl || !dataUrl.startsWith("data:")) return;

        // If this is already a previewable mime, no need to create blob
        if (
          mime &&
          (mime === "application/pdf" ||
            mime.startsWith("image/") ||
            mime.startsWith("text/"))
        ) {
          // revoke previous blob if any
          if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
            setObjectUrl(null);
          }
          return;
        }

        const parts = dataUrl.split(",");
        const header = parts[0];
        const b64 = parts.slice(1).join(",");
        let detectedMime =
          header.split(";")[0].replace("data:", "") ||
          mime ||
          "application/octet-stream";

        if (detectedMime === "application/octet-stream") {
          try {
            // decode as UTF-8 text
            const textProbe = new TextDecoder("utf-8", { fatal: true }).decode(
              Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)),
            );

            // If decode succeeds, it's text
            detectedMime = "text/plain";
          } catch {
            // Else Binary file so leave as octet-stream
          }
        }

        const byteCharacters = atob(b64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: detectedMime });
        const url = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
        setObjectUrl(url);
        setObjectMime(detectedMime);
      } catch (err) {
        console.error("Failed to prepare object URL for preview:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [normalizeToData]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 2,
        }}
      >
        {title || "View Document"}
        <IconButton onClick={onClose} size="small">
          <X className="w-5 h-5" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <CircularProgress />
          </div>
        ) : Array.isArray(documentUrl) && documentUrl.length > 0 ? (
          <div className="space-y-6">
            {documentUrl.map((doc, idx) => (
              <div key={idx}>
                <h3 className="mb-2 text-lg font-semibold">{doc.title}</h3>
                <iframe
                  src={doc.url}
                  title={doc.title}
                  style={{ width: "100%", height: "70vh", border: "none" }}
                />
              </div>
            ))}
          </div>
        ) : typeof documentUrl === "string" ? (
          (() => {
            // single URL case
            const normalized = normalizeToData;
            const src = normalized?.dataUrl || documentUrl;
            const mime = mimeType || normalized?.mime;
            // Check for Word documents first
            if (
              mime ===
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
              mime === "application/msword"
            ) {
              // For .docx, we use our docx-preview based viewer
              const urlToUse =
                objectUrl || (src.startsWith("blob:") ? src : null);
              if (urlToUse) {
                return <DocxViewer blobUrl={urlToUse} />;
              }
            }

            // If PDF or image or text-ish, iframe/embed will work
            // Also catch generic blob URLs if they weren't handled above
            if (
              (mime &&
                (mime === "application/pdf" ||
                  mime.startsWith("image/") ||
                  mime.startsWith("text/"))) ||
              (typeof src === "string" && src.startsWith("blob:"))
            ) {
              return (
                <iframe
                  src={src}
                  title={title}
                  style={{ width: "100%", height: "80vh", border: "none" }}
                />
              );
            }

            // For other types (eg .doc, .docx, msword) when we received a data: URL
            // we prepare an object URL in the effect above. Use that if available.
            if (src && src.startsWith("data:")) {
              if (objectUrl) {
                return (
                  <div style={{ width: "100%", height: "80vh" }}>
                    <object
                      data={objectUrl}
                      type={objectMime || "application/octet-stream"}
                      width="100%"
                      height="100%"
                    >
                      <div className="p-6 text-center">
                        <p className="mb-2">
                          Preview not available for this file type.
                        </p>
                        <a
                          href={objectUrl}
                          download={title || "document"}
                          className="underline"
                        >
                          Download the file
                        </a>
                        <p className="mt-2 text-sm text-gray-500">
                          You can open the downloaded file in Word or another
                          compatible app.
                        </p>
                      </div>
                    </object>
                  </div>
                );
              }

              // object URL not ready yet — show preparing and download fallback using data: URL
              return (
                <div
                  className="flex flex-col items-center justify-center"
                  style={{ height: "60vh" }}
                >
                  <CircularProgress />
                  <p className="mt-3">
                    Preparing preview... If this takes too long, download the
                    file below.
                  </p>
                  <a
                    href={src}
                    download={title || "document"}
                    className="mt-2 underline"
                  >
                    Download file
                  </a>
                </div>
              );
            }

            // Last-resort: remote URL or unknown mime — try iframe (may open in external viewer) or show download link
            return (
              <div className="p-6 text-center">
                <p className="mb-3">
                  Preview not available for this file type in the browser.
                </p>
                <a
                  href={src}
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                >
                  Open in new tab / download
                </a>
              </div>
            );
          })()
        ) : (
          <p className="text-center text-gray-500">No document to display.</p>
        )}
      </DialogContent>

      <DialogActions>
        <button
          onClick={onClose}
          className="px-4 py-2 border rounded-md hover:bg-gray-100"
        >
          Close
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewDocumentModal;
